import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const cardStyle = {
  maxWidth: 420,
  margin: '0 auto',
  padding: 32,
  borderRadius: 12,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  backgroundColor: '#fff',
  textAlign: 'center',
};

const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  backgroundColor: '#f5f5f7',
};

const titleStyle = {
  fontSize: 22,
  fontWeight: 600,
  marginBottom: 12,
  color: '#1a1a1a',
};

const textStyle = {
  fontSize: 15,
  color: '#666',
  lineHeight: 1.5,
  marginBottom: 20,
};

const errorTitleStyle = {
  ...titleStyle,
  color: '#c53030',
};

const buttonStyle = {
  display: 'inline-block',
  padding: '12px 24px',
  fontSize: 15,
  fontWeight: 500,
  color: '#fff',
  backgroundColor: '#2563eb',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  textDecoration: 'none',
};

const countdownStyle = {
  fontSize: 18,
  fontWeight: 600,
  color: '#16a34a',
  marginTop: 12,
};

export default function AuthConfirm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [countdown, setCountdown] = useState(3);
  const [errorMessage, setErrorMessage] = useState('');
  const executedRef = useRef(false);

  useEffect(() => {
    if (executedRef.current) return;
    executedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const token_hash = params.get('token_hash');
    const type = params.get('type') || 'signup';
    let next = params.get('next') || '/dashboard';

    // Validate next: must be a path starting with /
    if (!next.startsWith('/')) {
      next = '/dashboard';
    }

    if (!token_hash) {
      setStatus('error');
      setErrorMessage('Invalid confirmation link. The link may have expired or is incomplete.');
      return;
    }

    const verify = async () => {
      setStatus('loading');

      const { error } = await supabase.auth.verifyOtp({
        type,
        token_hash,
      });

      if (error) {
        setStatus('error');
        setErrorMessage(error.message || 'We couldn\'t confirm your email. Please try again or request a new link.');
        return;
      }

      // Ensure session exists (auto-login)
      let { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (!refreshData?.session) {
          navigate('/login?error=no_session', { replace: true });
          return;
        }
      }

      setStatus('success');
      setCountdown(3);
    };

    verify();
  }, [navigate]);

  useEffect(() => {
    if (status !== 'success') return;

    const params = new URLSearchParams(window.location.search);
    const next = (params.get('next') || '/dashboard').startsWith('/')
      ? (params.get('next') || '/dashboard')
      : '/dashboard';

    if (countdown <= 0) {
      navigate(next, { replace: true });
      return;
    }

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, navigate]);

  const handleBackToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {status === 'loading' && (
          <>
            <h2 style={titleStyle}>Confirming your account</h2>
            <p style={textStyle}>Please wait while we verify your email...</p>
            <div style={{ color: '#666', fontSize: 14 }}>● ● ●</div>
          </>
        )}

        {status === 'success' && (
          <>
            <h2 style={titleStyle}>Email confirmed ✅</h2>
            <p style={textStyle}>
              Taking you to your dashboard in {countdown} second{countdown !== 1 ? 's' : ''}...
            </p>
            <div style={countdownStyle}>{countdown}</div>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 style={errorTitleStyle}>We couldn't confirm your email</h2>
            <p style={textStyle}>
              {errorMessage}
            </p>
            <button
              type="button"
              style={buttonStyle}
              onClick={handleBackToLogin}
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
